import { Directive, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UkgTableData } from '../../utils/table-data-transformer';
import { ColumnVisibilityService } from '../../services/column-visibility.service';
import { GridColumn } from '../column-visibility-toggle/column-visibility-toggle.component';

interface CommonFacade {
  changePage(page: number): void;
  changePageSize(pageSize: number): void;
  sort(field: string): void;
  refresh(): void;
}

@Directive()
export abstract class BaseTableComponent implements OnDestroy {
  protected readonly destroy$ = new Subject<void>();
  protected readonly searchSubject = new Subject<string>();
  protected readonly columnFiltersSubject = new Subject<
    Record<string, string>
  >();
  protected readonly columnSearchSubjects = new Map<string, Subject<string>>();

  readonly Math = Math;

  protected readonly columnVisibilityService = inject(ColumnVisibilityService);
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly facade!: CommonFacade;

  hasLoadedOnce = false;
  searchTerm = '';
  columnFilters: Record<string, string> = {};
  currentSortField: string | null = null;
  currentSortDirection: 'asc' | 'desc' | null = null;

  showColumnPicker = false;
  protected columnLabelMap: Record<string, string> = {};
  protected abstract readonly columnVisibilityKeyName: string;

  get activeColumnFilterChips(): {
    key: string;
    label: string;
    value: string;
  }[] {
    return Object.entries(this.columnFilters)
      .filter(([, value]) => value?.trim())
      .map(([key, value]) => ({
        key,
        label: this.columnLabelMap[key] ?? key,
        value,
      }));
  }

  /**
   * Initialize search debouncing with custom handler
   */
  protected initializeSearch(
    searchHandler: (term: string) => void,
    debounceMs = 300
  ): void {
    this.searchSubject
      .pipe(
        debounceTime(debounceMs),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchHandler);
  }

  /**
   * Initialize column search with debouncing for each column
   */
  protected initializeColumnSearch(columns: string[], debounceMs = 300): void {
    columns.forEach(column => {
      this.columnFilters[column] = '';
      const subject = new Subject<string>();
      this.columnSearchSubjects.set(column, subject);

      subject
        .pipe(
          debounceTime(debounceMs),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.columnFiltersSubject.next({ ...this.columnFilters });
        });
    });
  }

  /**
   * Update search term and trigger search
   */
  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  /**
   * Clear search term and trigger search
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.searchSubject.next('');
  }

  /**
   * Update column filter and trigger filtering
   */
  onColumnSearch(columnKey: string, value: string): void {
    this.columnFilters[columnKey] = value;

    // If filter is cleared, immediately update without debounce
    if (!value || value.trim() === '') {
      this.columnFiltersSubject.next({ ...this.columnFilters });
      return;
    }

    // Otherwise use debounce for search values
    const subject = this.columnSearchSubjects.get(columnKey);
    if (subject) {
      subject.next(value);
    }
  }

  /**
   * Clear single column filter
   */
  clearColumnFilter(columnKey: string): void {
    this.columnFilters[columnKey] = '';
    const subject = this.columnSearchSubjects.get(columnKey);
    if (subject) {
      subject.next('');
    }
    this.columnFiltersSubject.next({ ...this.columnFilters });
  }

  /**
   * Clear all column filters
   */
  clearAllColumnFilters(): void {
    Object.keys(this.columnFilters).forEach(key => {
      this.columnFilters[key] = '';
      const subject = this.columnSearchSubjects.get(key);
      if (subject) {
        subject.next('');
      }
    });
    this.columnFiltersSubject.next({ ...this.columnFilters });
  }

  /**
   * Sort array data by a single column field
   */
  protected sortTableData(
    data: UkgTableData[],
    sortField: string | null,
    sortDirection: 'asc' | 'desc' | null
  ): UkgTableData[] {
    if (!data || !sortField || !sortDirection) return data;

    return [...data].sort((a, b) => {
      const aRaw = a.data?.[sortField];
      const bRaw = b.data?.[sortField];
      const aValue =
        typeof aRaw === 'object' && aRaw !== null
          ? (aRaw.text ?? '')
          : (aRaw ?? '');
      const bValue =
        typeof bRaw === 'object' && bRaw !== null
          ? (bRaw.text ?? '')
          : (bRaw ?? '');

      const aNum = Number(aValue);
      const bNum = Number(bValue);
      const isNumeric =
        !isNaN(aNum) &&
        !isNaN(bNum) &&
        String(aValue).trim() !== '' &&
        String(bValue).trim() !== '';

      const compare = isNumeric
        ? aNum - bNum
        : String(aValue)
            .toLowerCase()
            .localeCompare(String(bValue).toLowerCase());

      return sortDirection === 'asc' ? compare : -compare;
    });
  }

  /**
   * Filter array data by multiple column filters
   */
  protected filterTableData(
    data: UkgTableData[],
    columnFilters: Record<string, string>
  ): UkgTableData[] {
    if (!data || data.length === 0) return data;

    const activeFilters = Object.entries(columnFilters).filter(
      ([, value]) => value?.trim() !== ''
    );

    if (activeFilters.length === 0) return data;

    return data.filter(row =>
      activeFilters.every(([columnKey, filterValue]) => {
        const cellValue = row.data?.[columnKey];
        const cellText =
          typeof cellValue === 'string' ? cellValue : (cellValue?.text ?? '');
        return String(cellText ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      })
    );
  }

  /**
   * Handle table sort changes and update column sort indicators
   */

  protected onTableColumnSort(
    sortDetail: { key: string; isAsc: boolean | 'none' },
    columnNames: Record<string, unknown>
  ): void {
    if (sortDetail.isAsc === 'none') {
      this.currentSortField = null;
      this.currentSortDirection = null;
    } else {
      this.currentSortField = sortDetail.key;
      this.currentSortDirection = sortDetail.isAsc ? 'asc' : 'desc';
    }

    Object.entries(columnNames).forEach(([key, colObj]) => {
      if (colObj && typeof colObj === 'object' && 'sorted' in colObj) {
        (colObj as { sorted?: 'asc' | 'desc' | 'none' }).sorted =
          key === this.currentSortField
            ? (this.currentSortDirection as 'asc' | 'desc')
            : 'none';
      }
    });

    this.columnFiltersSubject.next({ ...this.columnFilters });
  }

  toggleColumnPicker(): void {
    this.showColumnPicker = !this.showColumnPicker;
  }

  toggleColumnVisibility(columns: GridColumn[]): void {
    this.columnVisibilityService.saveColumnVisibility(
      columns,
      this.columnVisibilityKeyName
    );
    this.cdr.markForCheck();
  }

  resetColumnVisibility(columns: GridColumn[]): void {
    this.columnVisibilityService.resetColumnVisibility(
      columns,
      this.columnVisibilityKeyName
    );
    this.cdr.markForCheck();
  }

  protected loadColumnVisibility(columns: GridColumn[]): void {
    this.columnVisibilityService.loadColumnVisibility(
      columns,
      this.columnVisibilityKeyName
    );
  }

  getCellValue(row: unknown, field: string): unknown {
    return (row as Record<string, unknown>)[field];
  }

  protected formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  onPageChange(page: number): void {
    this.facade.changePage(page);
  }

  onPageSizeChange(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  onSort(field: string): void {
    this.facade.sort(field);
  }

  onRetry(): void {
    this.facade.refresh();
  }

  protected createColumnLabelMap(
    columns: GridColumn[]
  ): Record<string, string> {
    const labelMap: Record<string, string> = {};
    columns.forEach(col => {
      labelMap[col.field] = col.header;
    });
    return labelMap;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
